/*
 * HOP: places, not posters, in the header search.
 *
 * Typing "la jolla" listed five separate posters plus "la jolla travel",
 * "la jolla wall art print"... The client wants ONE "La Jolla" row that
 * opens every La Jolla design. hopPlaces() below groups the posters Shopify
 * returns by the place in their title (the same rule as the map search in
 * hop-map-search.js) and swaps the dropdown for one row per place, linking
 * to /search?q=title:"La Jolla" (only posters whose title names the place).
 * Pressing Enter or "Search for ..." goes to that same page when the typed
 * text is the place. When no title names the typed text ("poster",
 * "gift"), the dropdown stays as Shopify built it.
 */
const hopPlaceSearch = (() => {
  function norm(s) {
    return String(s || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function parse(title) {
    const t = String(title || '');
    const place = t.split(/\s+Travel Poster/i)[0].trim();
    const m = t.match(/Travel Poster\s*[–—-]\s*(.+?)\s*Wall Art Print\s*$/i);
    return { place: place || t, detail: m ? m[1].trim() : '' };
  }

  // From the word where q starts to the end of the text, or null.
  function span(text, q) {
    const n = norm(text);
    let i = n.indexOf(q);
    if (i === -1) return null;
    if (i > 0 && n.charAt(i - 1) !== ' ' && n.charAt(i - 1) !== '-') {
      i = n.indexOf(' ' + q);
      if (i === -1) return null;
      i += 1;
    }
    const tidy = String(text).replace(/\s+/g, ' ').trim();
    const out =
      tidy.length === n.length
        ? tidy.slice(i)
        : n.slice(i).replace(/(^|[\s-])(\w)/g, (m, a, c) => a + c.toUpperCase());
    return out.replace(/[,\s]+$/, '');
  }

  // items: [{title, img}] -> [{key, label, count, img}]
  function groups(items, qRaw) {
    const q = norm(qRaw);
    if (q.length < 2) return [];
    const fromPlace = [];
    const fromDetail = [];
    items.forEach((p) => {
      const info = parse(p.title);
      const a = span(info.place, q);
      if (a) fromPlace.push({ name: a, img: p.img });
      else {
        const b = info.detail && span(info.detail, q);
        if (b) fromDetail.push({ name: b, img: p.img });
      }
    });
    const names = fromPlace.length ? fromPlace.concat(fromDetail) : fromDetail;
    names.sort((x, y) => norm(x.name).length - norm(y.name).length);
    const list = [];
    names.forEach(({ name, img }) => {
      const n = norm(name);
      let g = list.find((x) => n === x.key || n.indexOf(x.key + ' ') === 0);
      if (!g) {
        g = { key: n, label: name, count: 0, img: img };
        list.push(g);
      }
      g.count++;
      if (!g.img && img) g.img = img;
      if (n === g.key && /^[A-Z]/.test(name) && !/^[A-Z]/.test(g.label)) g.label = name;
    });
    list.sort((x, y) => {
      const sx = x.key.indexOf(q) === 0 ? 0 : 1;
      const sy = y.key.indexOf(q) === 0 ? 0 : 1;
      return sx - sy || y.count - x.count || x.key.localeCompare(y.key);
    });
    return list.slice(0, 5);
  }

  function url(label) {
    const base = (window.routes && window.routes.search_url) || '/search';
    return base + '?q=' + encodeURIComponent('title:"' + label + '"') + '&type=product&options%5Bprefix%5D=last';
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  return { norm, groups, url, esc };
})();
window.hopPlaceSearch = hopPlaceSearch;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.hopTopPlace = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    // HOP: Enter / "Search for" on a place name opens that place's page.
    const top = this.hopTopPlace;
    if (top && top.key === hopPlaceSearch.norm(this.getQuery())) {
      event.preventDefault();
      window.location.href = hopPlaceSearch.url(top.label);
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // HOP: ten posters (Shopify's most) so the place rows see more titles.
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&resources[limit]=10&resources[limit_scope]=each&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.hopPlaces();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  // HOP: one row per place in place of the poster list and the suggestions.
  hopPlaces() {
    this.hopTopPlace = null;
    const wrap = this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrap) return;
    const items = Array.from(wrap.querySelectorAll('#predictive-search-results-products-list li')).map((li) => {
      const h = li.querySelector('.predictive-search__item-heading');
      const img = li.querySelector('img');
      return { title: h ? h.textContent.trim() : '', img: img ? img.getAttribute('src') : '' };
    });
    const list = hopPlaceSearch.groups(items, this.getQuery());
    if (!list.length) return;
    this.hopTopPlace = list[0];
    const e = hopPlaceSearch.esc;
    const rows = list
      .map(
        (g, i) => `
      <li id="predictive-search-option-place-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${e(hopPlaceSearch.url(g.label))}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
          ${g.img ? `<img class="predictive-search__image" src="${e(g.img)}" alt="" width="50" height="62">` : ''}
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading h5">${e(g.label)}</p>
          </div>
        </a>
      </li>`
      )
      .join('');
    wrap.className =
      'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
    wrap.innerHTML = `
      <div class="predictive-search__result-group">
        <div>
          <h2 id="predictive-search-places" class="predictive-search__heading text-body caption-with-letter-spacing">Places</h2>
          <ul id="predictive-search-results-places-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-places">${rows}
          </ul>
        </div>
      </div>`;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
